import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import ElderlyIcon from '@mui/icons-material/Elderly';
import WomanIcon from '@mui/icons-material/Woman';
import { personaApi, userApi } from '../api';

type Persona = Awaited<ReturnType<typeof personaApi.getPersonas>>[number];

const getPersonaLabel = (persona: Persona): string => {
    return persona.name || persona.id || persona.description || 'Persona';
};

const getPersonaId = (persona: Persona): string => {
    return persona.id || persona.name || persona.created_at;
};

const getPersonaIcon = (persona: Persona) => {
    const label = `${persona.name || ''} ${persona.id || ''} ${persona.description || ''}`.toLowerCase();
    if (label.includes('mom') || label.includes('mother')) {
        return <WomanIcon sx={{ fontSize: 42 }} />;
    }
    if (label.includes('student')) {
        return <SchoolIcon sx={{ fontSize: 42 }} />;
    }
    if (label.includes('nana') || label.includes('grand') || label.includes('elder')) {
        return <ElderlyIcon sx={{ fontSize: 42 }} />;
    }
    return <PersonIcon sx={{ fontSize: 42 }} />;
};

const SelectPersonaPage = () => {
    const navigate = useNavigate();
    const [personas, setPersonas] = useState<Persona[]>([]);
    const [loading, setLoading] = useState(true);
    const [creatingUser, setCreatingUser] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedId, setSelectedId] = useState<string | null>(() => {
        const saved = localStorage.getItem('selectedPersona');
        if (!saved) {
            return null;
        }
        try {
            const parsed = JSON.parse(saved) as Persona;
            return getPersonaId(parsed);
        } catch {
            return null;
        }
    });

    useEffect(() => {
        // Whenever user lands on Select Persona, clear all persisted app state
        // so they truly start from scratch with a new persona.
        try {
            localStorage.clear();
            sessionStorage.removeItem('peer_app_initialized');
        } catch {
            // ignore storage errors
        }

        const loadPersonas = async () => {
            try {
                setLoading(true);
                const data = await personaApi.getPersonas();
                setPersonas(data);
                setError(null);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load personas.');
            } finally {
                setLoading(false);
            }
        };

        loadPersonas();
    }, []);

    const sortedPersonas = useMemo(() => {
        return [...personas].sort((a, b) => getPersonaLabel(a).localeCompare(getPersonaLabel(b)));
    }, [personas]);

    const handleSelectPersona = (persona: Persona) => {
        setSelectedId(getPersonaId(persona));
        localStorage.setItem('selectedPersona', JSON.stringify(persona));
    };

    const handleContinue = async () => {
        if (!selectedId || creatingUser) {
            return;
        }

        // Check if userId already exists in localStorage
        let userId = localStorage.getItem('userId');
        
        if (!userId) {
            setCreatingUser(true);
            setError(null);
            
            // Find the selected persona
            const selectedPersona = personas.find(p => getPersonaId(p) === selectedId);
            if (!selectedPersona) {
                setError('Selected persona not found');
                setCreatingUser(false);
                return;
            }

            try {
                // Create user with the selected persona (backend will generate the ID)
                const createdUser = await userApi.createUser({
                    persona_id: getPersonaId(selectedPersona),
                });
                
                // Store userId from backend response in localStorage
                localStorage.setItem('userId', createdUser.id.toString());
            } catch (err) {
                console.error('Failed to create user:', err);
                setError('Failed to create user. Please try again.');
                setCreatingUser(false);
                return;
            } finally {
                setCreatingUser(false);
            }
        }

        // Navigate to opening page
        navigate('/opening');
    };

    return (
        <Box
            sx={{
                width: '100vw',
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#FFFFFF',
                padding: '32px 20px 40px',
                boxSizing: 'border-box',
                position: 'relative',
            }}
        >
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#111111' }}>
                Choose a persona
            </Typography>

            {loading ? (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress />
                </Box>
            ) : personas.length === 0 ? (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" sx={{ color: '#666666' }}>
                        No personas available.
                    </Typography>
                </Box>
            ) : (
                <Box
                    sx={{
                        flex: 1,
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, auto)',
                            gap: '20px',
                            justifyContent: 'center',
                        }}
                    >
                    {sortedPersonas.map((persona) => {
                        const personaId = getPersonaId(persona);
                        const isSelected = selectedId === personaId;
                        return (
                            <Box
                                key={personaId}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '10px',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 72,
                                        height: 72,
                                        borderRadius: '50%',
                                        border: `3px solid ${isSelected ? '#EB0203' : '#E0E0E0'}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: isSelected ? '#EB0203' : '#9E9E9E',
                                    }}
                                >
                                    {getPersonaIcon(persona)}
                                </Box>
                                <Button
                                    variant={isSelected ? 'contained' : 'outlined'}
                                    size="small"
                                    onClick={() => handleSelectPersona(persona)}
                                    sx={{
                                        textTransform: 'none',
                                        borderRadius: '999px',
                                        fontWeight: 600,
                                        padding: '2px 14px',
                                        backgroundColor: isSelected ? '#EB0203' : '#FFFFFF',
                                        borderColor: '#EB0203',
                                        color: isSelected ? '#FFFFFF' : '#EB0203',
                                        '&:hover': {
                                            backgroundColor: isSelected ? '#C90102' : '#FDEAEA',
                                            borderColor: '#EB0203',
                                        },
                                    }}
                                >
                                    {isSelected ? 'Selected' : 'Select'}
                                </Button>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#333333' }}>
                                    {getPersonaLabel(persona)}
                                </Typography>
                            </Box>
                        );
                    })}
                    </Box>
                </Box>
            )}

            {error && (
                <Typography variant="body2" sx={{ color: '#C90102', position: 'absolute', top: '80px' }}>
                    {error}
                </Typography>
            )}

            {!loading && personas.length > 0 && (
                <Button
                    variant="contained"
                    onClick={handleContinue}
                    disabled={!selectedId || creatingUser}
                    sx={{
                        width: '100%',
                        maxWidth: '400px',
                        backgroundColor: '#EB0203',
                        color: '#FFFFFF',
                        fontWeight: 600,
                        padding: '12px 24px',
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontSize: '16px',
                        '&:hover': {
                            backgroundColor: '#C90102',
                        },
                        '&:disabled': {
                            backgroundColor: '#E0E0E0',
                            color: '#9E9E9E',
                        },
                    }}
                >
                    {creatingUser ? 'Creating user...' : 'Continue'}
                </Button>
            )}
        </Box>
    );
};

export default SelectPersonaPage;
